import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";  // bootstrap i18next before any component mounts
import App from "@/App";

// ----------------------------------------------------------------------------
// Google Translate ↔ React DOM reconciliation safety patch
//
// PROBLEM: Google Translate wraps text nodes in <font> tags as it mutates the
// DOM in place. When React later tries to update or unmount one of those
// nodes (e.g. routing to /exam/take, swapping a question, hiding a modal),
// React's internal pointer no longer matches what's actually in the DOM and
// it throws:
//   "Failed to execute 'removeChild' on 'Node':
//    The node to be removed is not a child of this node."
// In our app this surfaces as: switch portal language → Google Translate
// kicks in → open an exam → the page crashes into the AppErrorBoundary
// (which itself shows the translated error template, hence the Shona text
// in the user's screenshot).
//
// FIX: Wrap `Node.prototype.removeChild` and `Node.prototype.insertBefore`
// so that when React asks to remove/insert a node whose parent has been
// changed *behind its back* by Google Translate, we no-op instead of
// throwing. This is the canonical workaround recommended by the React team
// (facebook/react#11538). It runs BEFORE React mounts so every subsequent
// reconciliation is protected. It does NOT suppress real bugs — the
// invariant we relax is exactly the one Google Translate violates.
// ----------------------------------------------------------------------------
if (typeof Node === 'function' && Node.prototype) {
  const origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      // Google Translate moved this node — let React think the removal
      // succeeded so the next reconciliation can recover.
      return child;
    }
    return origRemoveChild.apply(this, arguments);
  };
  const origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return origInsertBefore.apply(this, arguments);
  };
  // iter-376 — also harden replaceChild. React itself uses removeChild +
  // insertBefore most of the time, but some library code (animation libs,
  // portals, third-party widgets) hits replaceChild. Same Google-Translate
  // foreign-parent failure mode applies, same no-op recovery.
  const origReplaceChild = Node.prototype.replaceChild;
  Node.prototype.replaceChild = function (newChild, oldChild) {
    if (oldChild && oldChild.parentNode !== this) {
      return oldChild;
    }
    return origReplaceChild.apply(this, arguments);
  };
}

// Suppress errors that originate inside browser extensions (MetaMask, ad
// blockers, password managers, etc.). They inject scripts into every page and
// regularly throw errors that have NOTHING to do with our code — but CRA's
// dev error overlay catches them and shows a scary "Uncaught runtime errors"
// banner that misleads users. Production users see the same noise in their
// console / Sentry reports. We swallow only errors whose stack/source points
// at a `chrome-extension://` / `moz-extension://` / `safari-web-extension://`
// origin; everything else (real app bugs) is left alone.
const isExtensionError = (errOrEvent) => {
  try {
    const stack = String(errOrEvent?.error?.stack || errOrEvent?.reason?.stack || errOrEvent?.stack || '');
    const filename = String(errOrEvent?.filename || '');
    const message = String(errOrEvent?.message || errOrEvent?.reason?.message || '');
    const haystack = `${stack} ${filename} ${message}`;
    return /chrome-extension:\/\/|moz-extension:\/\/|safari-web-extension:\/\/|MetaMask/i.test(haystack);
  } catch {
    return false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (isExtensionError(e)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
  window.addEventListener('unhandledrejection', (e) => {
    if (isExtensionError(e)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}

// Service Worker Registration with Update Handling
//
// IMPORTANT: We only register the service worker in PRODUCTION builds.
// In development (CRA `npm start` / preview hot-reload), the SW aggressively
// caches JS/CSS bundles that get rebuilt constantly. The cached chunks then
// reference stale module IDs and produce blank pages on soft navigations
// (only hard-refresh fixes it). Skipping SW in dev avoids the trap entirely.
//
// We also actively unregister any leftover SW from prior dev sessions so the
// developer doesn't have to manually clear it from DevTools.
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('[App] Service workers not supported');
    return;
  }

  if (process.env.NODE_ENV !== 'production') {
    // Clean up any old SW registered by previous deploys when the same hostname
    // hosts both preview (dev) and production builds at different times.
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        await reg.unregister();
      }
      if (regs.length) {
        console.log('[App] Unregistered', regs.length, 'stale dev service workers');
        // Drop any caches the previous SW left behind
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.filter((k) => k.startsWith('eventflow')).map((k) => caches.delete(k)));
        }
      }
    } catch (e) {
      console.warn('[App] SW cleanup skipped:', e);
    }
    console.log('[App] SW registration skipped in development');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none' // Always fetch fresh service worker
    });

    console.log('[App] Service Worker registered:', registration.scope);

    // Check for updates immediately
    registration.update();

    // Handle update found
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      console.log('[App] New service worker installing...');

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New version available - the UpdateNotification component will handle the prompt
            console.log('[App] New version available');
            
            // Dispatch custom event for React components to listen to
            window.dispatchEvent(new CustomEvent('sw-update-available', {
              detail: { registration }
            }));
          } else {
            // First install
            console.log('[App] App ready for offline use');
          }
        }
      });
    });

    // Periodic update check (every 60 seconds)
    setInterval(() => {
      registration.update().catch(console.error);
    }, 60 * 1000);

    // Listen for controlling service worker change.
    // IMPORTANT: only reload when there was ALREADY a controller — a fresh
    // page whose SW is installing for the first time will also trigger
    // `controllerchange` via `clients.claim()`, and reloading on that
    // event puts the browser into an infinite spinner loop.
    const initialController = navigator.serviceWorker.controller;
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing || !initialController) return;
      refreshing = true;
      console.log('[App] Controller changed, reloading...');
      window.location.reload();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_ACTIVATED') {
        console.log('[App] New service worker activated:', event.data.version);
      }
    });

  } catch (error) {
    console.error('[App] Service Worker registration failed:', error);
  }
};

// Detect if running as installed PWA
const isInstalledPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

// Log app mode
if (isInstalledPWA()) {
  console.log('[App] Running as installed PWA');
  document.body.classList.add('pwa-standalone');
} else {
  console.log('[App] Running in browser');
}

// Initialize service worker
registerServiceWorker();

// Handle online/offline status
window.addEventListener('online', () => {
  console.log('[App] Back online');
  document.body.classList.remove('offline');
  window.dispatchEvent(new CustomEvent('app-online'));
});

window.addEventListener('offline', () => {
  console.log('[App] Went offline');
  document.body.classList.add('offline');
  window.dispatchEvent(new CustomEvent('app-offline'));
});

// Version check on visibility change (when user returns to app)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // Check for updates when app becomes visible
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch(console.error);
      });
    }
  }
});

// Render React app
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
